/**
 * Product Analytics custom actions (Datadog).
 *
 * Datadog Product Analytics consumes the RUM stream this app already produces, so there is no
 * second SDK and no second session: a "product event" is just a RUM custom action. Two things
 * are needed on top of the automatic instrumentation — stable names for the clicks the SDK
 * collects on its own (`data-dd-action-name` on the controls themselves, which the SDK reads by
 * default) and the handful of business events below, which are not clicks at all but outcomes.
 *
 * Consent is honoured for free, and deliberately so rather than by re-reading the banner's
 * state: RUM is only ever initialised after the visitor accepts the `analytics` category
 * (`DatadogRUM.tsx`, H2-204), so `getInitConfiguration()` returning undefined *is* the
 * "no consent yet, or withdrawn before it was ever given" case. Asking the SDK is one source of
 * truth instead of two that can disagree.
 *
 * @see https://docs.datadoghq.com/product_analytics/
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/tracking_user_actions/
 */

import { loadedDatadog } from './datadog-sdk';
import { logger } from './logger';

// Build-time flag, matching `DatadogRUM`: with Datadog off nothing is loaded and nothing is sent.
const DD_ENABLED = import.meta.env.VITE_DD_ENABLED === 'true';

/**
 * @desc The product events this app reports, snake_case, named for what happened rather than
 * for the control that caused it — a name is a funnel step in Datadog and outlives the button.
 */
export type ProductAction =
  | 'prompt_sent'
  | 'new_chat_started'
  | 'artifact_downloaded'
  | 'artifact_viewer_opened'
  | 'response_feedback_given'
  | 'response_copied';

export type ProductActionAttributes = Record<string, string | number | boolean>;

/**
 * @desc Report one product event to Datadog RUM as a custom action.
 *
 * A no-op when Datadog is disabled at build time or RUM has not been initialised (i.e. no
 * analytics consent). Never throws: instrumentation must not be able to break the interaction
 * it is measuring, so an SDK failure is logged and swallowed.
 *
 * Attributes are dimensions, never content — no prompt text, no response text, no file
 * contents. Anything unbounded belongs in a length or a count.
 */
export function trackAction(name: ProductAction, attributes?: ProductActionAttributes): void {
  // The SDK loads lazily on consent (`datadog-sdk.ts`): not loaded is "no consent yet" too.
  const datadogRum = loadedDatadog()?.datadogRum;
  if (!DD_ENABLED || !datadogRum || datadogRum.getInitConfiguration() === undefined) return;

  try {
    datadogRum.addAction(name, attributes);
  } catch (error) {
    logger.warn('[Analytics] Failed to report action', {
      action: name,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
